import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateInterestConfigThunk,
  updatePaymentRulesThunk,
  updateKYCRequirementsThunk,
  updatePausePolicyThunk,
} from "../../../store/thunks/branchInstallmentThunks";
import { selectIsSavingConfig } from "../../../store/selectors/branchInstallmentSelectors";
import { PencilIcon, CheckIcon, XIcon } from "@heroicons/react/outline";

const InstallmentConfigSection = ({ branchCode, config }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector(selectIsSavingConfig);

  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData(config[section] || {});
  };

  const handleCancel = () => {
    setEditingSection(null);
    setFormData({});
  };

  const handleSave = async (section) => {
    try {
      let thunk;
      switch (section) {
        case "interestConfig":
          thunk = updateInterestConfigThunk({
            branchCode,
            interestConfig: formData,
          });
          break;
        case "paymentRules":
          thunk = updatePaymentRulesThunk({
            branchCode,
            paymentRules: formData,
          });
          break;
        case "kycRequirements":
          thunk = updateKYCRequirementsThunk({
            branchCode,
            kycRequirements: formData,
          });
          break;
        case "pausePolicy":
          thunk = updatePausePolicyThunk({ branchCode, pausePolicy: formData });
          break;
        default:
          return;
      }

      await dispatch(thunk).unwrap();
      setEditingSection(null);
      setFormData({});
    } catch (error) {
      alert(`Failed to update: ${error}`);
    }
  };

  const handleChange = (e, path) => {
    const { value, type, checked } = e.target;
    const keys = path.split(".");

    setFormData((prev) => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] =
        type === "checkbox"
          ? checked
          : type === "number"
          ? Number(value)
          : value;

      return newData;
    });
  };

  const renderEditButtons = (section) => (
    <div className="flex gap-2">
      {editingSection === section ? (
        <>
          <button
            onClick={() => handleSave(section)}
            disabled={isSaving}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
          >
            <CheckIcon className="h-5 w-5" />
          </button>
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </>
      ) : (
        <button
          onClick={() => handleEdit(section)}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          <PencilIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Interest Configuration */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Interest Configuration</h3>
          {renderEditButtons("interestConfig")}
        </div>

        {editingSection === "interestConfig" ? (
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.chargeInterest}
                onChange={(e) => handleChange(e, "chargeInterest")}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">Charge Interest</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Calculation Method
              </label>
              <select
                value={formData.calculationMethod}
                onChange={(e) => handleChange(e, "calculationMethod")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="FLAT">Flat</option>
                <option value="REDUCING">Reducing</option>
                <option value="SIMPLE">Simple</option>
                <option value="NONE">None</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Annual Rate (%)
                </label>
                <input
                  type="number"
                  value={formData.defaultRate?.annualRate}
                  onChange={(e) => handleChange(e, "defaultRate.annualRate")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  step="0.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grace Period (Days)
                </label>
                <input
                  type="number"
                  value={formData.gracePeriodDays}
                  onChange={(e) => handleChange(e, "gracePeriodDays")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Penalty Interest</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.penaltyInterest?.enabled}
                    onChange={(e) => handleChange(e, "penaltyInterest.enabled")}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">
                    Enable Penalty Interest
                  </span>
                </label>

                {formData.penaltyInterest?.enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Rate (%)
                      </label>
                      <input
                        type="number"
                        value={formData.penaltyInterest?.additionalRate}
                        onChange={(e) =>
                          handleChange(e, "penaltyInterest.additionalRate")
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        step="0.1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Apply After Days
                      </label>
                      <input
                        type="number"
                        value={formData.penaltyInterest?.applyAfterDays}
                        onChange={(e) =>
                          handleChange(e, "penaltyInterest.applyAfterDays")
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Calculation Method</p>
              <p className="font-semibold">
                {config.interestConfig?.calculationMethod}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Default Rate</p>
              <p className="font-semibold">
                {config.interestConfig?.defaultRate?.annualRate}% p.a.
              </p>
            </div>
            <div>
              <p className="text-gray-600">Grace Period</p>
              <p className="font-semibold">
                {config.interestConfig?.gracePeriodDays} days
              </p>
            </div>
            <div>
              <p className="text-gray-600">Penalty Interest</p>
              <p className="font-semibold">
                {config.interestConfig?.penaltyInterest?.enabled
                  ? `+${config.interestConfig.penaltyInterest.additionalRate}%`
                  : "Disabled"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* KYC Requirements */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">KYC Requirements</h3>
          {renderEditButtons("kycRequirements")}
        </div>

        {editingSection === "kycRequirements" ? (
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.mandatory}
                onChange={(e) => handleChange(e, "mandatory")}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">KYC Mandatory</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Order Value for KYC
              </label>
              <input
                type="number"
                value={formData.minOrderValueForKYC}
                onChange={(e) => handleChange(e, "minOrderValueForKYC")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.requiresManualVerification}
                onChange={(e) => handleChange(e, "requiresManualVerification")}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">
                Requires Manual Verification
              </span>
            </label>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Mandatory</p>
              <p className="font-semibold">
                {config.kycRequirements?.mandatory ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Min Order Value</p>
              <p className="font-semibold">
                ₹{config.kycRequirements?.minOrderValueForKYC?.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Manual Verification</p>
              <p className="font-semibold">
                {config.kycRequirements?.requiresManualVerification
                  ? "Required"
                  : "Not Required"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Payment Rules */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Payment Rules</h3>
          {renderEditButtons("paymentRules")}
        </div>

        {editingSection === "paymentRules" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Installment Amount
                </label>
                <input
                  type="number"
                  value={formData.minInstallmentAmount}
                  onChange={(e) => handleChange(e, "minInstallmentAmount")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fixed Due Date (Day of Month)
                </label>
                <input
                  type="number"
                  value={formData.fixedDueDate}
                  onChange={(e) => handleChange(e, "fixedDueDate")}
                  min="1"
                  max="31"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Prepayment</h4>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.prepayment?.allowed}
                  onChange={(e) => handleChange(e, "prepayment.allowed")}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium">Allow Prepayment</span>
              </label>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Min Installment Amount</p>
              <p className="font-semibold">
                ₹{config.paymentRules?.minInstallmentAmount?.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Fixed Due Date</p>
              <p className="font-semibold">
                Day {config.paymentRules?.fixedDueDate}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Prepayment</p>
              <p className="font-semibold">
                {config.paymentRules?.prepayment?.allowed
                  ? "Allowed"
                  : "Not Allowed"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pause Policy */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Pause Policy</h3>
          {renderEditButtons("pausePolicy")}
        </div>

        {editingSection === "pausePolicy" ? (
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.allowed}
                onChange={(e) => handleChange(e, "allowed")}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">Allow Pause</span>
            </label>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Pauses Allowed
                </label>
                <input
                  type="number"
                  value={formData.maxPausesAllowed}
                  onChange={(e) => handleChange(e, "maxPausesAllowed")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Consecutive Months
                </label>
                <input
                  type="number"
                  value={formData.maxConsecutivePauseMonths}
                  onChange={(e) => handleChange(e, "maxConsecutivePauseMonths")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Total Months
                </label>
                <input
                  type="number"
                  value={formData.maxTotalPauseMonths}
                  onChange={(e) => handleChange(e, "maxTotalPauseMonths")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.requiresApproval}
                onChange={(e) => handleChange(e, "requiresApproval")}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">Requires Approval</span>
            </label>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Allowed</p>
              <p className="font-semibold">
                {config.pausePolicy?.allowed ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Max Pauses Allowed</p>
              <p className="font-semibold">
                {config.pausePolicy?.maxPausesAllowed}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Max Consecutive Months</p>
              <p className="font-semibold">
                {config.pausePolicy?.maxConsecutivePauseMonths}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Requires Approval</p>
              <p className="font-semibold">
                {config.pausePolicy?.requiresApproval ? "Yes" : "No"}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallmentConfigSection;
